import React from 'react';
import PopupMenu from '@com-pc/popup-menu/index';
import ShapeMenu from '@com-pc/shape-pick-menu/index';
import { ActionType } from '@src/framework/editor/src/js/constants/common-action-type';
import { MenuType } from '@src/slide/domain/ui-model/constant/menu';
import ChartLayoutMenu from '@src/framework/frame/components/tool-bar/menu/chart-layout-menu';
import GuidesColorPanel from '@com/guides-line/guides-color-panel';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { InsertTableRowItem } from '@src/slide/application/service/menu-manager/menu-items/insert-table-row-item';
import { IRightMenuManager, IRightMenuManangerConfig, IRightMenuManangerLastParams } from './right-menu-manager.interface';
import { IMenuManager } from '@src/slide/application/service/menu-manager/menu-manager.interface';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { EventPosition, TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import ArkLoader from '@src/js/common/ark-extensions';

/**
 * 右键菜单管理
 */
export class RightMenuManager implements IRightMenuManager {
    public menuMap: Map<string, any> = new Map();
    public disableHide = false;
    public lastConfig: IRightMenuManangerLastParams|null = null;
    private menuManager: IMenuManager;
    private selectionService: ISelectionService;

    public setServicesDelegate(services: { menuManager?: IMenuManager; selectionService?: ISelectionService }): void {
        if (!this.menuManager && services.menuManager) {
            this.menuManager = services.menuManager;
        }
        if (!this.selectionService && services.selectionService) {
            this.selectionService = services.selectionService;
        }
    }

    /**
     * 显示菜单
     * @param modelType  右键编辑区类型
     * @param data 菜单数据项数组
     * @param config 配置项，包括弹出位置，top,left,right等等,如 fix：true固定位置，scrollable：true，超过800可滚动
     * @param onMenuItemClickCallBack(MenuType,itemID)
     */
    public showMenu(
        modelType,
        data,
        disableIds,
        config: IRightMenuManangerConfig = {},
        onMenuItemClickCallBack,
    ) {
        const { menuMap } = this;
        this.lastConfig = { modelType, data, disableIds, config, onMenuItemClickCallBack };
        menuMap.forEach((value, key) => {
            if (key !== modelType) {
                value.hide();  // 隐藏其他正在显示的菜单
            }
        });

        let menuOper = menuMap.get(modelType);
        const clickCallBack = function (itemID) {
            if (onMenuItemClickCallBack) {
                onMenuItemClickCallBack(MenuType.POP_MENU, itemID);
            }
        };
        if (!menuOper) {
            const id = `right-menu-id-${modelType}`;
            if (config) {
                config.id = id;
            }
            menuOper = PopupMenu.createMenu(null, config, clickCallBack);
            menuMap.set(modelType, menuOper);
        }
        menuOper.updateMenuData(data);
        menuOper.clearAllDisableState();
        if (disableIds && disableIds instanceof Array) {
            menuOper.setDisableWithIds(disableIds.map(item => item.id));
        }

        if (modelType === UIModelType.EMPTY) {
            config.selectable = true;
        }
        menuOper.show(config, clickCallBack);
        this.setCustomElement(menuOper, modelType);
        this.setSelectedElement(menuOper);
        this.reportMenuShow();
    }

    public setSelectedElement(menuOper: any) {
        if (this.menuManager?.guidesManager?.displayGuides) {
            menuOper?.setSelectedWithId?.(ActionType.DISPLAY_GUIDES);
        } else {
            menuOper?.setUnSelectedWithId?.(ActionType.DISPLAY_GUIDES);
        }

        if (this.menuManager?.snapLineService?.enableState) {
            menuOper?.setSelectedWithId?.(ActionType.TOGGLE_SNAP_LINE);
        } else {
            menuOper?.setUnSelectedWithId?.(ActionType.TOGGLE_SNAP_LINE);
        }

        if (this.menuManager?.textRulerManager?.displayRuler) {
            menuOper?.setSelectedWithId?.(ActionType.DISPLAY_TEXT_RULER);
        } else {
            menuOper?.setUnSelectedWithId?.(ActionType.DISPLAY_TEXT_RULER);
        }

        if (this.menuManager?.gridLineManager?.displayGridLine) {
            menuOper?.setSelectedWithId?.(ActionType.DISPLAY_GRID_LINE);
        } else {
            menuOper?.setUnSelectedWithId?.(ActionType.DISPLAY_GRID_LINE);
        }

        if (this.menuManager?.gridLineManager?.isGridAlign) {
            menuOper?.setSelectedWithId?.(ActionType.GRID_LINE_ALIGN);
        } else {
            menuOper?.setUnSelectedWithId?.(ActionType.GRID_LINE_ALIGN);
        }
    }

    public setCustomElement(menuOper: any, modelType: string) {
        if (menuOper.hasMenuId(ActionType.TAILOR_PIC_SELECT_SHAPE)) {
            menuOper.setCustomElement(
                ActionType.TAILOR_PIC_SELECT_SHAPE,
                <ShapeMenu
                    hideMenu={() => this.hideMenu(modelType)}
                    cropPicker={true}/>,
            );
        }
        if (menuOper.hasMenuId(ActionType.GUIDE_COLOR_PANEL)) {
            menuOper.setCustomElement(
                ActionType.GUIDE_COLOR_PANEL,
                <GuidesColorPanel menuManager={this.menuManager} />,
            );
        }
        // 临时注释，快速布局截图效果差。视觉4月中旬提供，到时候发布
        if (menuOper.hasMenuId(ActionType.MODIFY_CHART_LAYOUT_CONTENT)) {
            menuOper.setCustomElement(
                ActionType.MODIFY_CHART_LAYOUT_CONTENT,
                <ChartLayoutMenu
                    hideMenu={() => this.hideMenu(modelType)}
                    menuManager={this.menuManager}
                    selectionService={this.selectionService}
                />,
            );
        }
        this.setTableCustomMenuItem(menuOper);
    }

    public setTableCustomMenuItem(menuOper: any) {
        if (menuOper.hasMenuId(ActionType.INSERT_ROW_TOP)) {
            menuOper.setClickableCustomElement(
                ActionType.INSERT_ROW_TOP,
                <InsertTableRowItem
                    type={ActionType.INSERT_ROW_TOP}
                    menuManager={this.menuManager} />,
            );
        }
        if (menuOper.hasMenuId(ActionType.INSERT_ROW_BOTTOM)) {
            menuOper.setClickableCustomElement(
                ActionType.INSERT_ROW_BOTTOM,
                <InsertTableRowItem
                    type={ActionType.INSERT_ROW_BOTTOM}
                    menuManager={this.menuManager} />,
            );
        }
        if (menuOper.hasMenuId(ActionType.INSERT_COL_LEFT)) {
            menuOper.setClickableCustomElement(
                ActionType.INSERT_COL_LEFT,
                <InsertTableRowItem
                    type={ActionType.INSERT_COL_LEFT}
                    menuManager={this.menuManager} />,
            );
        }
        if (menuOper.hasMenuId(ActionType.INSERT_COL_RIGHT)) {
            menuOper.setClickableCustomElement(
                ActionType.INSERT_COL_RIGHT,
                <InsertTableRowItem
                    type={ActionType.INSERT_COL_RIGHT}
                    menuManager={this.menuManager} />,
            );
        }
    }

    public hideMenu(modelType: string) {
        const menuOper = this.menuMap.get(modelType);
        if (menuOper) {
            menuOper.hide();
        }
        this.lastConfig = null;
    }

    public hideAllMenu() {
        if (this.disableHide) {
            this.disableHide = false;
            return;
        }
        // eslint-disable-next-line no-restricted-syntax
        for (const [key, value] of this.menuMap) {
            console.log('key|value=', key, value);
            if (value) {
                value.hide();
            }
        }
        this.lastConfig = null;
    }

    public disableHideAll() {
        this.disableHide = true;
    }

    // 移除菜单
    public RemoveMenu(modelType: string) {
        const menuOper = this.menuMap.get(modelType);
        if (menuOper) {
            this.menuMap.delete(modelType);
            menuOper.remove();
        }
        this.lastConfig = null;
    }

    private reportMenuShow() {
        const vipInfo = ArkLoader.extensionService.dependentServiceCombinder?.accountApplication?.vipInfo;
        tdw({
            opername: TDWOperationType.DOC_VIP_EDITOR,
            module: TDWModuleType.RIGHT_MENU,
            action: TDWActionType.SHOW,
            ver5: vipInfo.type,
            ver9: EventPosition.PC_RIGHT_MENU,
            ver12: 'slide',
        });
    }
}
